import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "../App";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle,
} from "./ui/dialog";
import {
  FileSignature, Plus, RefreshCw, Trash2, Eye, CheckCircle2, Clock,
  Loader2, Search, X, FileUp,
} from "lucide-react";
import BulkDocsUploadDialog from "./BulkDocsUploadDialog";

const fmtDateTime = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString("pt-BR"); }
  catch { return iso; }
};

const brl = (v) => {
  if (v == null) return "—";
  const n = Number(v);
  if (!Number.isFinite(n)) return "—";
  return n.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
};

// ============================================================
// Create contract dialog
// ============================================================
function CreateContractDialog({ open, onOpenChange, onCreated, adminToken }) {
  const [employees, setEmployees] = useState([]);
  const [search, setSearch] = useState("");
  const [employeeId, setEmployeeId] = useState("");
  const [tipo, setTipo] = useState("indeterminado");
  const [funcao, setFuncao] = useState("VIGILANTE");
  const [posto, setPosto] = useState("");
  const [salario, setSalario] = useState("");
  const [inicio, setInicio] = useState("");
  const [termino, setTermino] = useState("");
  const [jornada, setJornada] = useState("Conforme escala");
  const [cidade, setCidade] = useState("Salvador/BA");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setEmployeeId(""); setFuncao("VIGILANTE"); setPosto("");
    setTipo("indeterminado");
    setSalario(""); setInicio(""); setTermino("");
    setJornada("Conforme escala"); setCidade("Salvador/BA");
    setSearch("");
    axios.get(`${API}/admin/employees`, {
      headers: { Authorization: `Bearer ${adminToken}` },
      params: { limit: 1000 },
    }).then((r) => {
      const list = Array.isArray(r.data) ? r.data
        : (r.data?.employees || []);
      setEmployees(list);
    }).catch(() => setEmployees([]));
  }, [open, adminToken]);

  const filtered = employees.filter((e) => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (
      (e.nome || "").toLowerCase().includes(q) ||
      (e.matricula || "").toLowerCase().includes(q) ||
      (e.cpf || "").includes(q)
    );
  }).slice(0, 50);

  const selected = employees.find((e) => e.id === employeeId);

  const submit = async () => {
    if (!employeeId) return toast.error("Selecione um funcionário");
    if (!funcao.trim()) return toast.error("Função obrigatória");
    if (!salario) return toast.error("Salário obrigatório");
    if (!inicio) return toast.error("Data de início obrigatória");
    if (tipo !== "indeterminado" && !termino)
      return toast.error("Data de término obrigatória");
    setBusy(true);
    try {
      const res = await axios.post(`${API}/admin/contracts/create`,
        {
          employee_id: employeeId,
          tipo,
          funcao: funcao.trim(),
          posto: posto.trim(),
          salario: Number(String(salario).replace(",", ".")),
          inicio: inicio.trim(),
          termino: termino.trim(),
          jornada: jornada.trim(),
          cidade_assinatura: cidade.trim(),
        },
        {
          headers: { Authorization: `Bearer ${adminToken}` },
          timeout: 30000,
        },
      );
      toast.success("Contrato criado com sucesso");
      onCreated && onCreated(res.data);
      onOpenChange(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Falha ao criar contrato");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="contract-create-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileSignature className="w-5 h-5 text-sky-700" />
            Novo Contrato de Trabalho
          </DialogTitle>
          <DialogDescription>
            Preencha os dados do contrato. O sistema gera o PDF moderno
            automaticamente e o disponibiliza para o funcionário assinar.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          {/* Employee picker */}
          <div className="rounded-md border border-slate-200 bg-slate-50 p-3 space-y-2">
            <Label className="text-xs font-semibold text-slate-700">
              Funcionário
            </Label>
            <div className="relative">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input
                placeholder="Buscar por nome, matrícula ou CPF…"
                className="pl-9 bg-white"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                data-testid="contract-employee-search"
              />
            </div>
            {selected ? (
              <div className="flex items-center justify-between rounded-md border border-emerald-300 bg-emerald-50 p-2">
                <div className="text-xs">
                  <div className="font-semibold text-emerald-900">
                    {selected.nome}
                  </div>
                  <div className="text-emerald-700 font-mono">
                    Mat. {selected.matricula} · CPF {selected.cpf}
                  </div>
                </div>
                <button
                  onClick={() => setEmployeeId("")}
                  className="text-emerald-700 hover:text-emerald-900"
                  data-testid="contract-employee-clear"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            ) : (
              <div className="max-h-40 overflow-y-auto rounded-md border border-slate-200 bg-white">
                {filtered.length === 0 ? (
                  <div className="p-3 text-xs text-slate-500 text-center">
                    {search ? "Nenhum funcionário encontrado." : "Carregando…"}
                  </div>
                ) : filtered.map((e) => (
                  <button
                    key={e.id}
                    onClick={() => setEmployeeId(e.id)}
                    className="w-full text-left p-2 border-b border-slate-100 last:border-0 hover:bg-slate-50 transition text-xs"
                    data-testid={`contract-employee-${e.matricula}`}
                  >
                    <div className="font-medium">{e.nome}</div>
                    <div className="text-slate-500 font-mono">
                      Mat. {e.matricula} · CPF {e.cpf}
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Tipo de contrato */}
          <div className="rounded-md border border-slate-200 bg-slate-50 p-3">
            <Label className="text-xs font-semibold text-slate-700 mb-2 block">
              Tipo de Contrato
            </Label>
            <div className="grid grid-cols-3 gap-2">
              <button
                type="button"
                onClick={() => setTipo("indeterminado")}
                data-testid="contract-tipo-indeterminado"
                className={`text-left rounded-md border p-2.5 transition ${
                  tipo === "indeterminado"
                    ? "border-sky-600 bg-white shadow-sm"
                    : "border-slate-200 bg-white/50 hover:bg-white"
                }`}
              >
                <div className="text-xs font-semibold text-sky-800">
                  Prazo Indeterminado
                </div>
                <div className="text-[10px] text-slate-500 mt-0.5">
                  CLT Art. 442 · 443 · Regra geral
                </div>
              </button>
              <button
                type="button"
                onClick={() => setTipo("experiencia")}
                data-testid="contract-tipo-experiencia"
                className={`text-left rounded-md border p-2.5 transition ${
                  tipo === "experiencia"
                    ? "border-sky-600 bg-white shadow-sm"
                    : "border-slate-200 bg-white/50 hover:bg-white"
                }`}
              >
                <div className="text-xs font-semibold text-sky-800">
                  Experiência
                </div>
                <div className="text-[10px] text-slate-500 mt-0.5">
                  CLT Art. 443 §2º · Máx. 90 dias
                </div>
              </button>
              <button
                type="button"
                onClick={() => setTipo("determinado")}
                data-testid="contract-tipo-determinado"
                className={`text-left rounded-md border p-2.5 transition ${
                  tipo === "determinado"
                    ? "border-sky-600 bg-white shadow-sm"
                    : "border-slate-200 bg-white/50 hover:bg-white"
                }`}
              >
                <div className="text-xs font-semibold text-sky-800">
                  Prazo Determinado
                </div>
                <div className="text-[10px] text-slate-500 mt-0.5">
                  CLT Art. 443 §2º · Lei 9.601/1998
                </div>
              </button>
            </div>
          </div>

          {/* Contract fields */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Função</Label>
              <Input value={funcao}
                     onChange={(e) => setFuncao(e.target.value)}
                     placeholder="VIGILANTE"
                     data-testid="contract-funcao-input" />
            </div>
            <div>
              <Label className="text-xs">Posto / Seção</Label>
              <Input value={posto}
                     onChange={(e) => setPosto(e.target.value)}
                     placeholder="SERPRO"
                     data-testid="contract-posto-input" />
            </div>
            <div>
              <Label className="text-xs">Salário (R$)</Label>
              <Input value={salario}
                     onChange={(e) => setSalario(e.target.value)}
                     placeholder="2271,74"
                     inputMode="decimal"
                     data-testid="contract-salario-input" />
            </div>
            <div>
              <Label className="text-xs">Jornada</Label>
              <Input value={jornada}
                     onChange={(e) => setJornada(e.target.value)}
                     placeholder="Conforme escala"
                     data-testid="contract-jornada-input" />
            </div>
            <div>
              <Label className="text-xs">Início (DD/MM/AAAA)</Label>
              <Input value={inicio}
                     onChange={(e) => setInicio(e.target.value)}
                     placeholder="01/06/2026"
                     data-testid="contract-inicio-input" />
            </div>
            <div>
              <Label className="text-xs">
                Término (DD/MM/AAAA){tipo === "indeterminado" && " — opcional"}
              </Label>
              <Input value={termino}
                     onChange={(e) => setTermino(e.target.value)}
                     placeholder={tipo === "indeterminado"
                       ? "— (não se aplica)" : "15/07/2026"}
                     disabled={tipo === "indeterminado"}
                     data-testid="contract-termino-input" />
            </div>
            <div className="col-span-2">
              <Label className="text-xs">Cidade da assinatura</Label>
              <Input value={cidade}
                     onChange={(e) => setCidade(e.target.value)}
                     placeholder="Salvador/BA"
                     data-testid="contract-cidade-input" />
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}
                  disabled={busy} data-testid="contract-cancel-btn">
            Cancelar
          </Button>
          <Button onClick={submit} disabled={busy}
                  className="bg-sky-700 hover:bg-sky-800 text-white"
                  data-testid="contract-create-submit-btn">
            {busy ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <FileSignature className="w-4 h-4 mr-2" />
            )}
            Gerar contrato
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


// ============================================================
// Main tab
// ============================================================
export default function AdminContractsTab({ adminToken }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [filter, setFilter] = useState("");
  const [previewUrl, setPreviewUrl] = useState("");
  const [previewName, setPreviewName] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/contracts`, {
        headers: { Authorization: `Bearer ${adminToken}` },
        timeout: 15000,
      });
      setRows(r.data || []);
    } catch {
      toast.error("Falha ao carregar contratos");
    } finally {
      setLoading(false);
    }
  }, [adminToken]);

  useEffect(() => { load(); }, [load]);

  const filtered = rows.filter((r) => {
    if (!filter) return true;
    const q = filter.toLowerCase();
    return (
      (r.employee_nome || "").toLowerCase().includes(q) ||
      (r.employee_matricula || "").toLowerCase().includes(q) ||
      (r.funcao || "").toLowerCase().includes(q)
    );
  });

  const openPreview = async (row) => {
    try {
      const r = await axios.get(
        `${API}/admin/contracts/${row.id}/preview`,
        {
          headers: { Authorization: `Bearer ${adminToken}` },
          responseType: "blob", timeout: 30000,
        },
      );
      const url = URL.createObjectURL(r.data);
      setPreviewUrl(url);
      setPreviewName(`Contrato — ${row.employee_nome || row.id.slice(0, 8)}`);
    } catch {
      toast.error("Não foi possível abrir o PDF");
    }
  };

  const closePreview = () => {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPreviewUrl(""); setPreviewName("");
  };

  const remove = async (row) => {
    if (!window.confirm(`Excluir contrato de ${row.employee_nome}?`)) return;
    try {
      await axios.delete(`${API}/admin/contracts/${row.id}`, {
        headers: { Authorization: `Bearer ${adminToken}` },
      });
      toast.success("Contrato excluído");
      load();
    } catch {
      toast.error("Falha ao excluir");
    }
  };

  const pending = rows.filter((r) => r.status !== "signed").length;
  const signed = rows.filter((r) => r.status === "signed").length;

  return (
    <div className="space-y-4" data-testid="admin-contracts-tab">
      {/* Header */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h2 className="text-lg font-bold text-slate-900 flex items-center gap-2">
            <FileSignature className="w-5 h-5 text-sky-700" />
            Contratos de Trabalho
          </h2>
          <p className="text-xs text-slate-500">
            Gerencie e disponibilize contratos para assinatura eletrônica
            dos funcionários.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={load}
                  disabled={loading} data-testid="contracts-refresh-btn">
            {loading ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <RefreshCw className="w-4 h-4 mr-2" />
            )}
            Atualizar
          </Button>
          <Button variant="outline" size="sm" onClick={() => setBulkOpen(true)}
                  data-testid="contracts-bulk-upload-btn">
            <FileUp className="w-4 h-4 mr-2" />
            Importar PDF
          </Button>
          <Button size="sm" onClick={() => setCreateOpen(true)}
                  className="bg-sky-700 hover:bg-sky-800 text-white"
                  data-testid="contracts-new-btn">
            <Plus className="w-4 h-4 mr-2" />
            Novo Contrato
          </Button>
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-3 gap-3">
        <div className="rounded-md border border-slate-200 bg-white p-3">
          <div className="text-[10px] text-slate-500 uppercase font-bold">Total</div>
          <div className="text-2xl font-bold text-slate-900" data-testid="contracts-kpi-total">
            {rows.length}
          </div>
        </div>
        <div className="rounded-md border border-amber-200 bg-amber-50 p-3">
          <div className="text-[10px] text-amber-700 uppercase font-bold">Pendentes</div>
          <div className="text-2xl font-bold text-amber-900" data-testid="contracts-kpi-pending">
            {pending}
          </div>
        </div>
        <div className="rounded-md border border-emerald-200 bg-emerald-50 p-3">
          <div className="text-[10px] text-emerald-700 uppercase font-bold">Assinados</div>
          <div className="text-2xl font-bold text-emerald-900" data-testid="contracts-kpi-signed">
            {signed}
          </div>
        </div>
      </div>

      {/* Filter */}
      <div className="relative">
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
        <Input placeholder="Filtrar por nome, matrícula ou função…"
               className="pl-9"
               value={filter}
               onChange={(e) => setFilter(e.target.value)}
               data-testid="contracts-filter-input" />
      </div>

      {/* Table */}
      <div className="overflow-x-auto rounded-md border border-slate-200 bg-white">
        <table className="w-full text-xs">
          <thead className="bg-slate-50">
            <tr>
              <th className="px-3 py-2 text-left">Funcionário</th>
              <th className="px-3 py-2 text-left">Função / Posto</th>
              <th className="px-3 py-2 text-right">Salário</th>
              <th className="px-3 py-2 text-center">Vigência</th>
              <th className="px-3 py-2 text-center">Status</th>
              <th className="px-3 py-2 text-center">Criado em</th>
              <th className="px-3 py-2 text-right">Ações</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr>
                <td colSpan={7} className="px-3 py-8 text-center text-slate-500">
                  {rows.length === 0
                    ? 'Nenhum contrato. Clique em "Novo Contrato" acima.'
                    : "Nenhum contrato corresponde ao filtro."}
                </td>
              </tr>
            ) : filtered.map((r) => (
              <tr key={r.id} className="border-t border-slate-100"
                  data-testid={`contracts-row-${r.id}`}>
                <td className="px-3 py-2">
                  <div className="font-medium text-slate-900">
                    {r.employee_nome || "—"}
                  </div>
                  <div className="text-[10px] text-slate-500 font-mono">
                    Mat. {r.employee_matricula || "—"}
                  </div>
                </td>
                <td className="px-3 py-2">
                  <div className="font-medium">{r.funcao || "—"}</div>
                  <div className="text-[10px] text-slate-500">
                    {(r.data?.posto) || "—"}
                  </div>
                  {r.tipo === "indeterminado" && (
                    <Badge variant="outline" className="mt-1 text-[9px] px-1 py-0 bg-emerald-50 text-emerald-700 border-emerald-200">
                      Indeterminado
                    </Badge>
                  )}
                  {r.tipo === "determinado" && (
                    <Badge variant="outline" className="mt-1 text-[9px] px-1 py-0 bg-violet-50 text-violet-700 border-violet-200">
                      Determinado
                    </Badge>
                  )}
                  {r.tipo === "experiencia" && (
                    <Badge variant="outline" className="mt-1 text-[9px] px-1 py-0 bg-sky-50 text-sky-700 border-sky-200">
                      Experiência
                    </Badge>
                  )}
                </td>
                <td className="px-3 py-2 text-right font-mono">
                  {brl(r.salario)}
                </td>
                <td className="px-3 py-2 text-center text-[10px]">
                  <div>{r.inicio || "—"}</div>
                  <div className="text-slate-500">a {r.termino || "—"}</div>
                </td>
                <td className="px-3 py-2 text-center">
                  {r.status === "signed" ? (
                    <Badge className="bg-emerald-100 text-emerald-800 border-emerald-300">
                      <CheckCircle2 className="w-3 h-3 mr-1" />
                      Assinado
                    </Badge>
                  ) : (
                    <Badge variant="outline" className="text-amber-700 border-amber-300">
                      <Clock className="w-3 h-3 mr-1" />
                      Pendente
                    </Badge>
                  )}
                </td>
                <td className="px-3 py-2 text-center text-[10px]">
                  {fmtDateTime(r.created_at)}
                </td>
                <td className="px-3 py-2 text-right">
                  <div className="flex items-center justify-end gap-1">
                    <Button variant="ghost" size="sm" onClick={() => openPreview(r)}
                            data-testid={`contracts-preview-${r.id}`}>
                      <Eye className="w-4 h-4" />
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => remove(r)}
                            data-testid={`contracts-delete-${r.id}`}>
                      <Trash2 className="w-4 h-4 text-red-600" />
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Preview Dialog */}
      <Dialog open={!!previewUrl} onOpenChange={(v) => !v && closePreview()}>
        <DialogContent className="max-w-5xl w-[95vw] h-[90vh] flex flex-col">
          <DialogHeader>
            <DialogTitle>{previewName}</DialogTitle>
          </DialogHeader>
          {previewUrl && (
            <iframe src={previewUrl} title="Contract preview"
                    className="flex-1 w-full rounded-md border border-slate-200" />
          )}
        </DialogContent>
      </Dialog>

      <CreateContractDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        onCreated={load}
        adminToken={adminToken}
      />

      <BulkDocsUploadDialog open={bulkOpen} onOpenChange={setBulkOpen}
        kind="contract" adminToken={adminToken}
        title="Importar Contratos do Folhamatic"
        accentClassName="bg-sky-700 hover:bg-sky-800"
        onConfirmed={load} />
    </div>
  );
}
